import type { BadgeView, PublicBadge } from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession, can } from '../../lib/session';

/** Score badges (decision 0032): the business's public link to its CyberScore, and the badge image. */

const key = (tenant: string | undefined, id: string) => ['assessments', tenant, id, 'badge'];

/** The assessment's live badge, or null when it isn't shared. */
export function useBadge(id: string) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: key(tenant?.id, id),
    queryFn: () => api<BadgeView | null>('GET', `/assessments/${id}/badge`),
    enabled: !!tenant && !!id,
  });
}

/** Only the business owner shares the score: it is the business's to show. */
export function useCanShare() {
  const { tenant } = useSession();
  return can(tenant, 'sharing.create');
}

export function useShareBadge(id: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (share: boolean) =>
      share
        ? api<BadgeView>('POST', `/assessments/${id}/badge`)
        : api<null>('POST', `/assessments/${id}/badge/revoke`),
    onSuccess: (view) => qc.setQueryData(key(tenant?.id, id), view),
  });
}

type Where = Pick<Location, 'origin' | 'pathname'>;

/** The page anyone with the link opens, and the image other websites show. */
export function badgeLinks(token: string, where: Where = window.location) {
  return {
    page: `${where.origin}${where.pathname}#/b/${token}`,
    image: `${where.origin}/api/public/badges/${token}/badge.svg`,
  };
}

/** An HTML snippet for a website or an email signature: the badge, linking to its public page. */
export function badgeSnippet(token: string, business: string, where: Where = window.location) {
  const { page, image } = badgeLinks(token, where);
  const alt = `CyberScore badge for ${business}`.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);
  return `<a href="${page}"><img src="${image}" alt="${alt}" width="300" height="96"></a>`;
}

/** What the public page shows: no sign-in, so plain fetch, not the signed-in API client. */
export async function fetchPublicBadge(token: string): Promise<PublicBadge | null> {
  const res = await fetch(`/api/public/badges/${encodeURIComponent(token)}`, { headers: { accept: 'application/json' } }); // prettier-ignore
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(res.status === 429 ? 'Too many requests. Try again in a minute.' : `The badge could not be loaded (${res.status}).`); // prettier-ignore
  return (await res.json()) as PublicBadge;
}
